<template>
  <div class="upload">
    <h5 v-if="label" class="primary-2--text">{{ label }}</h5>
    <label
      :class="
        `upload-area ${
          !!result ? (result === 'error' ? 'is-error' : 'is-success') : ''
        }`
      "
      v-cloak
      @drop.prevent="addFile"
      @dragover.prevent
    >
      <input type="file" @change="addFile" ref="file" :multiple="multiple" />
      <div v-if="!preview && !previewFile" class="upload-input">
        <v-icon color="primary-1" size="40">mdi-file-upload</v-icon>
        <br />
        <div class="mt-2">
          {{ buttonText }}
        </div>
      </div>
      <template v-else-if="!previewFile">
        <div v-if="result === 'error'" class="error-label">
          <span class="mr-3">
            <img src="/image/ic-warning@3x.png" width="40" />
            上傳失敗
          </span>
          <div class="upload-input">重新上傳檔案</div>
        </div>
      </template>
      <figure v-else :class="`upload-preview text-center`" @click="reset">
        <v-img :src="previewFile" contain :style="{ maxHeight: '420px' }" />
        <v-btn class="delete-btn" depressed>
          <v-icon color="white" size="24">mdi-trash-can-outline</v-icon>
        </v-btn>
      </figure>
    </label>
    <h5 class="input-note" v-if="!!note">{{ note }}</h5>
  </div>
</template>

<script>
export default {
  name: "FileUpload",
  props: {
    label: {
      type: String,
      default: ""
    },
    multiple: {
      type: Boolean,
      default: false
    },
    deleteBtn: {
      type: Boolean,
      default: false
    },
    dragText: {
      type: String,
      default: "拖曳圖片到這裡 或"
    },
    buttonText: {
      type: String,
      default: "選擇檔案"
    },
    name: {
      type: String,
      default: null
    },
    result: {
      type: String,
      default: null
    },
    note: {
      type: String,
      default: ""
    },
    default: {
      default: null
    },
    preview: {
      default: null
    },
    change: {
      type: Function,
      default: null
    }
  },
  data() {
    return {
      timestamp: new Date().valueOf(),
      files: null,
      previewFile: this.default || null
    };
  },
  watch: {
    default: {
      handler(newVal) {
        if (newVal) this.previewFile = URL.createObjectURL(newVal);
      },
      immediate: true
    }
  },
  methods: {
    addFile(e) {
      if (e.type === "change") {
        this.files = e.target.files[0];
        // 使用 canvas 壓縮圖片到 5MB 以下
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const img = new Image();
        img.src = URL.createObjectURL(this.files);

        const maxSize = 4.4 * 1024 * 1024; // 5MB
        const base64Overhead = 1.33; // Base64 編碼增加的大小比例
        let fileScale =
          this.files.size > maxSize / base64Overhead
            ? maxSize / (this.files.size * base64Overhead)
            : 0.8;
        fileScale = Math.min(Math.max(fileScale, 0.1), 1);

        img.onload = async () => {
          // 設定最大寬高
          const maxWidth = 1600;
          const maxHeight = 1200;
          let { width, height } = img;

          // 等比例縮放
          if (width > maxWidth || height > maxHeight) {
            const widthRatio = maxWidth / width;
            const heightRatio = maxHeight / height;
            const ratio = Math.min(widthRatio, heightRatio);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            blob => {
              // blob 轉回 File
              const name = this.files.name.split(".");
              this.files = new File(
                [blob],
                `${name[0]}.${blob.type.split("/")[1]}`,
                {
                  type: blob.type
                }
              );
              this.$emit("change", this.name, this.files);
            },
            "image/jpeg",
            fileScale
          );
        };
      }
      // console.log(this.$emit);
    },
    reset() {
      this.files = null;
      this.previewFile = null;
      this.$refs.file.value = null;
      this.$emit("change", this.name, null);
    },
    removeFile(file) {
      this.files = this.files.filter(f => {
        return f != file;
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.v-btn:not(.v-btn--round).v-size--default.delete-btn {
  min-width: 0;
  width: 40px;
  height: 40px;
  position: absolute;
  right: 8px;
  bottom: 8px;
  padding: 0;
  background-color: rgba(0, 0, 0, 0.3);
}
.upload {
  margin: 16px auto;
  // max-width: 500px;
  h5 {
    margin-bottom: 1rem;
  }

  .upload-preview {
    position: absolute;
    width: 100%;
    height: auto;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    margin: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    img {
      max-width: 100%;
    }

    .result {
      position: absolute;
      right: 40px;
      top: 30px;
      display: block;
      width: 40px;
      height: 40px;
      background: white;
      border-radius: 50%;
    }
  }

  .upload-area {
    position: relative;
    display: flex;
    align-items: center;
    align-content: center;
    justify-items: center;
    justify-content: center;
    text-align: center;
    color: #8d8d8d;
    font-size: 14px;
    font-weight: 600;
    padding: 0 32px;
    padding-bottom: 66.6666667%;
    border-radius: 4px;
    border: dashed 2px #41b6e6;
    background-color: rgba(65, 182, 230, 0.1);
    text-align: center;

    &.is-error {
      background-color: #ffe5e6;

      .error-label {
        color: #ff7074;

        .upload-input {
          color: #ff7074;
          background: white;
          border: 2px solid #ff7074;
        }
      }
    }

    &.is-success {
      background-color: rgba(40, 189, 146, 0.15);
      border: 0;

      .success-label {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        color: rgba(68, 68, 68, 1);

        .upload-input {
          border: 0;
          background: none;
          color: rgba(68, 68, 68, 1);
        }
      }
    }
    figure figcaption {
      color: #444;
    }
  }

  .upload-input {
    position: absolute;
    display: inline-block;
    color: #000;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    width: 100%;
    height: 65px;
    margin: auto;
  }

  input {
    position: absolute;
    opacity: 0;
  }

  .input-note {
    margin: 15px 0 0;
    padding: 15px 14px;
    border-radius: 4px;
    font-size: 16px;
    font-weight: 500;
    background-color: rgba(253, 218, 36, 0.12);
  }
}
</style>
